/**
 * 성취기준 일괄등록 모달: 엑셀(.xlsx)/CSV 파일 → 편제·영역·성취기준·수준 A~E 행 파싱 (브라우저 전용)
 * - .xlsx: 기존 의존성 exceljs 를 동적 import (번들 분리)
 * - .csv: 자체 파서 (UTF-8 → 깨지면 EUC-KR 재시도)
 */

export type ImportedStandardRow = {
  track: string;
  area: string;
  achievement: string;
  /** 수준 A~E 순 */
  levels: string[];
};

export type ImportedSheet = {
  name: string;
  rows: ImportedStandardRow[];
};

/** 앱 전반 구분점은 '·' 사용 → '⋅'(U+22C5)·'•'·'ㆍ' 등 정규화 */
export function normalizeImportText(raw: string): string {
  return raw
    .replace(/[\u22C5\u2027\u30FB\u318D\uFF65]/g, '·')
    .replace(/\r\n?/g, '\n')
    .replace(/[ \t\u00A0]+/g, ' ')
    .split('\n')
    .map((l) => l.trim())
    .join('\n')
    .trim();
}

/** 성취기준 코드 [4국01-01] 추출 */
export function extractAchievementCode(text: string): string {
  const m = /\[\s*([^\]\s][^\]]*?)\s*\]/.exec(text ?? '');
  return m ? m[1].replace(/\s+/g, '') : '';
}

function normHeader(s: string): string {
  return s.replace(/[\s\-_·.:()（）]/g, '').toUpperCase();
}

type ColumnMap = { track: number; area: number; achievement: number; levels: number[] };

function detectHeader(grid: string[][]): { headerRow: number; map: ColumnMap } | null {
  const limit = Math.min(grid.length, 15);
  for (let r = 0; r < limit; r += 1) {
    const cells = grid[r].map((c) => normHeader(c ?? ''));
    const find = (pred: (h: string) => boolean) => cells.findIndex(pred);
    const track = find((h) => h === '편제' || h === '교과' || h === '과목');
    const area = find((h) => h === '영역');
    const achievement = find((h) => h === '성취기준' || h === '성취기준내용');
    if (achievement < 0 || (track < 0 && area < 0)) continue;
    const levels: number[] = [];
    'ABCDE'.split('').forEach((letter) => {
      const idx = find((h) => h === `수준${letter}` || h === `성취수준${letter}` || h === letter);
      if (idx >= 0) levels.push(idx);
    });
    return { headerRow: r, map: { track, area, achievement, levels } };
  }
  return null;
}

export function parseStandardGrid(grid: string[][], allowFallback = true): ImportedStandardRow[] {
  const detected = detectHeader(grid);
  /** 헤더를 못 찾으면: 단일 시트/CSV 는 앞 6열 순서 가정, 여러 시트 중 하나(예: 출처)는 데이터 없음 처리 */
  if (!detected && !allowFallback) return [];
  const map: ColumnMap = detected?.map ?? { track: 0, area: 1, achievement: 2, levels: [3, 4, 5] };
  const start = detected ? detected.headerRow + 1 : 0;
  const out: ImportedStandardRow[] = [];
  for (let r = start; r < grid.length; r += 1) {
    const row = grid[r] ?? [];
    const at = (i: number) => (i >= 0 ? normalizeImportText(String(row[i] ?? '')) : '');
    const item: ImportedStandardRow = {
      track: at(map.track),
      area: at(map.area),
      achievement: at(map.achievement),
      levels: map.levels.map((i) => at(i)),
    };
    const empty = !item.track && !item.area && !item.achievement && item.levels.every((v) => !v);
    if (empty) continue;
    /** 헤더 반복 행 스킵 */
    if (normHeader(item.achievement) === '성취기준') continue;
    if (!item.achievement) continue;
    out.push(item);
  }
  return out;
}

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  const firstLine = text.split(/\r?\n/, 1)[0] ?? '';
  const delim = (firstLine.match(/\t/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0) ? '\t' : ',';
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else quoted = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"') quoted = true;
    else if (ch === delim) {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  if (cell || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}

function excelCellText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === 'object') {
    const v = value as Record<string, unknown>;
    if (Array.isArray(v.richText)) return (v.richText as Array<{ text?: string }>).map((t) => t.text ?? '').join('');
    if ('result' in v) return excelCellText(v.result);
    if ('text' in v) return excelCellText(v.text);
  }
  return '';
}

export async function readStandardSheetsFromFile(file: File): Promise<ImportedSheet[]> {
  const lower = file.name.toLowerCase();
  if (lower.endsWith('.csv') || lower.endsWith('.tsv') || lower.endsWith('.txt')) {
    const buf = await file.arrayBuffer();
    let text = new TextDecoder('utf-8').decode(buf);
    if (text.includes('\uFFFD')) {
      try {
        text = new TextDecoder('euc-kr').decode(buf);
      } catch {
        /* keep utf-8 */
      }
    }
    text = text.replace(/^\uFEFF/, '');
    return [{ name: file.name.replace(/\.[^.]+$/, ''), rows: parseStandardGrid(parseCsv(text)) }];
  }
  if (lower.endsWith('.xls')) {
    throw new Error('XLS_UNSUPPORTED');
  }
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  const sheets: ImportedSheet[] = [];
  wb.worksheets.forEach((ws) => {
    const grid: string[][] = [];
    ws.eachRow({ includeEmpty: true }, (row, rowNumber) => {
      const cells: string[] = [];
      const values = row.values as unknown[];
      for (let c = 1; c < values.length; c += 1) cells.push(excelCellText(values[c]));
      grid[rowNumber - 1] = cells;
    });
    for (let i = 0; i < grid.length; i += 1) if (!grid[i]) grid[i] = [];
    sheets.push({ name: ws.name, rows: parseStandardGrid(grid, wb.worksheets.length === 1) });
  });
  return sheets;
}

/** 기본 선택 시트: 학년군(예: 3-4학년)이 클래스 학년을 포함 → '전체' → 데이터 있는 첫 시트 */
export function pickDefaultSheetName(sheets: ImportedSheet[], gradeNumbers: number[] = []): string | null {
  const withData = sheets.filter((s) => s.rows.length > 0);
  if (withData.length === 0) return null;
  if (gradeNumbers.length > 0) {
    const band = withData.find((s) => {
      const m = /(\d)\s*[-~∼]\s*(\d)/.exec(s.name);
      if (!m) return false;
      const lo = Number(m[1]);
      const hi = Number(m[2]);
      return gradeNumbers.every((g) => g >= lo && g <= hi);
    });
    if (band) return band.name;
  }
  const all = withData.find((s) => s.name.replace(/\s/g, '') === '전체');
  return (all ?? withData[0]).name;
}

/** 카테고리명이 편제명과 같거나 포함하면 그 편제를 기본 필터로 */
export function pickDefaultTrackFilter(tracks: string[], categoryName: string): string {
  const cat = categoryName.replace(/\s/g, '');
  if (!cat) return '';
  const exact = tracks.find((t) => t.replace(/\s/g, '') === cat);
  if (exact) return exact;
  const contained = [...tracks].sort((a, b) => b.length - a.length).find((t) => t && cat.includes(t.replace(/\s/g, '')));
  return contained ?? '';
}
